import { Slot } from 'radix-ui';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import { Spinner } from './Spinner.js';
import './button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'hero';
export type ControlSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * `hero` is the brand-gradient call to action: at most one per screen
   * (BUILD-PLAN 3.4). `primary` carries the theme's action colour.
   */
  variant?: ButtonVariant;
  size?: ControlSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Shows a spinner, keeps the width, and ignores clicks. */
  loading?: boolean;
  block?: boolean;
  /** Render the single child (for example a router link) with button styling. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    loading = false,
    block = false,
    asChild = false,
    className,
    children,
    type,
    onClick,
    ...rest
  },
  ref,
) {
  const shared = {
    className: cx('vel-btn', className),
    'data-variant': variant,
    'data-size': size,
    'data-loading': loading ? '' : undefined,
    'data-block': block ? '' : undefined,
    'aria-busy': loading || undefined,
  };
  if (asChild) {
    return (
      <Slot.Root ref={ref} {...shared} {...rest}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-disabled={loading || undefined}
      onClick={(e) => {
        if (loading) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      {...shared}
      {...rest}
    >
      {leadingIcon ? (
        <span className="vel-btn__icon" aria-hidden>
          {leadingIcon}
        </span>
      ) : null}
      {children !== undefined && children !== null ? (
        <span className="vel-btn__label">{children}</span>
      ) : null}
      {trailingIcon ? (
        <span className="vel-btn__icon" aria-hidden>
          {trailingIcon}
        </span>
      ) : null}
      {loading ? (
        <span className="vel-btn__spinner">
          <Spinner />
        </span>
      ) : null}
    </button>
  );
});
